import React, { useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import {
    Box, Chip, IconButton, Menu, MenuItem,
    Stack, Tooltip, Typography, ListItemIcon, ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import LoadingComponent from '../../../common/components/LoadingComponent';
import DeleteModule from './DeleteModule';
import { useGroupList } from '../../../common/API/hooks';
import { getMajorGroupMeta, getModuleGroupLabel } from './moduleGrouping';

const BRAND = '#0F2FEB';

const tableStyles = {
    rows: {
        style: {
            minHeight: '3.8rem',
            '&:hover': { backgroundColor: '#F3F6FF !important' },
        },
    },
    headRow: {
        style: {
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            backgroundColor: '#F8F9FA',
            borderBottom: '2px solid #E0E0E0',
        },
    },
    pagination: { style: { backgroundColor: '#fff' } },
    expanderRow: { style: { backgroundColor: '#FAFBFF' } },
};

function RowActions({ row, onEditModule }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const menuOpen = Boolean(anchorEl);
    const openMenu = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
    const closeMenu = () => setAnchorEl(null);
    const run = (fn) => { closeMenu(); fn(); };

    return (
        <>
            <Tooltip title="Actions" arrow>
                <IconButton size="small" onClick={openMenu} sx={{ color: 'text.secondary' }}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={closeMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{ elevation: 3, sx: { minWidth: 180, borderRadius: 2, mt: 0.5 } }}
            >
                <MenuItem onClick={() => run(() => onEditModule(row))}>
                    <ListItemIcon><EditRoundedIcon fontSize="small" sx={{ color: BRAND }} /></ListItemIcon>
                    <ListItemText>Edit Module</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => run(() => setDeleteOpen(true))} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteRoundedIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Delete Module</ListItemText>
                </MenuItem>
            </Menu>

            {/* Pass row directly — DeleteModule expects module.row.id */}
            <DeleteModule module={{ row }} isOpen={deleteOpen} setsetIsOpen={setDeleteOpen} />
        </>
    );
}

function ExpandedRow({ data }) {
    const { data: groupList } = useGroupList();
    const [hoveredUser, setHoveredUser] = useState(null);
    const [hoveredGroup, setHoveredGroup] = useState(null);

    const highlightedUsers = useMemo(() => {
        if (!groupList || !hoveredGroup) return [];
        return groupList.find(g => g.name === hoveredGroup)?.users || [];
    }, [hoveredGroup, groupList]);

    const highlightedGroups = useMemo(() => {
        if (!groupList || !hoveredUser) return [];
        return groupList.filter(g => g.users.includes(hoveredUser)).map(g => g.name);
    }, [hoveredUser, groupList]);

    const renderChipGroup = (items, highlight, onEnter, onLeave, onClick, color = 'default') =>
        items.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {items.map((item, i) => (
                    <Chip
                        key={i}
                        label={item}
                        size="small"
                        variant={highlight.includes(item) ? 'filled' : 'outlined'}
                        color={highlight.includes(item) ? 'warning' : color}
                        sx={{
                            fontSize: '0.7rem', height: 22, cursor: 'pointer',
                            transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={() => onEnter(item)}
                        onMouseLeave={() => onLeave()}
                        onClick={() => onClick(item)}
                    />
                ))}
            </Box>
        ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">None</Typography>
        );

    return (
        <Box sx={{ px: 4, py: 2.5, bgcolor: '#FAFBFF', borderTop: '1px solid #E8EAF6' }}>
            <Stack spacing={2.5}>
                {/* Allowed Users */}
                <Box>
                    <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1 }}>
                        <PeopleAltRoundedIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                        <Typography variant="overline" color="text.secondary"
                            sx={{ letterSpacing: '0.08em', fontWeight: 700 }}>
                            Directly Allowed Users ({data.allowed_users?.length || 0})
                        </Typography>
                    </Stack>
                    {renderChipGroup(
                        [...(data.allowed_users || [])].sort(),
                        highlightedUsers,
                        setHoveredUser,
                        () => setHoveredUser(null),
                        (u) => setHoveredUser(u === hoveredUser ? null : u),
                        'default',
                    )}
                </Box>

                {/* Allowed Groups */}
                <Box>
                    <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1 }}>
                        <GroupsRoundedIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                        <Typography variant="overline" color="text.secondary"
                            sx={{ letterSpacing: '0.08em', fontWeight: 700 }}>
                            Allowed Groups ({data.allowed_groups?.length || 0})
                        </Typography>
                    </Stack>
                    {renderChipGroup(
                        [...(data.allowed_groups || [])].sort(),
                        highlightedGroups,
                        setHoveredGroup,
                        () => setHoveredGroup(null),
                        (g) => setHoveredGroup(g === hoveredGroup ? null : g),
                        'primary',
                    )}
                    {data.allowed_groups?.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Hover a group to highlight its members, or hover a user to highlight their groups.
                        </Typography>
                    )}
                </Box>

                {/* Effective visibility */}
                <Box>
                    <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1 }}>
                        <PeopleAltRoundedIcon sx={{ fontSize: '0.9rem', color: '#2E7D32' }} />
                        <Typography variant="overline" sx={{ letterSpacing: '0.08em', fontWeight: 700, color: '#2E7D32' }}>
                            Effectively Visible To ({data.visibleToUsers?.length || 0})
                        </Typography>
                    </Stack>
                    {data.is_public ? (
                        <Chip label="All Users" size="small" color="success" sx={{ fontSize: '0.7rem', height: 22 }} />
                    ) : (
                        renderChipGroup(
                            data.visibleToUsers || [],
                            highlightedUsers,
                            setHoveredUser,
                            () => setHoveredUser(null),
                            (u) => setHoveredUser(u === hoveredUser ? null : u),
                            'default',
                        )
                    )}
                </Box>
            </Stack>
        </Box>
    );
}

export default function ModuleTable({ filteredModules, isLoading, onEditModule }) {
    const columns = [
        {
            name: 'MODULE NAME',
            selector: row => row.name,
            sortable: true,
            minWidth: '260px',
            grow: 2,
            cell: row => (
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ p: 0.7, borderRadius: 1.5, bgcolor: '#EEF1FD', display: 'flex', flexShrink: 0 }}>
                        <ExtensionRoundedIcon sx={{ color: BRAND, fontSize: '1.1rem' }} />
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                        {row.name}
                    </Typography>
                </Stack>
            ),
        },
        {
            name: 'MAJOR GROUP',
            selector: row => row.majorGroupLabel || getMajorGroupMeta(row.moduleGroup || 'ungrouped').label,
            sortable: true,
            minWidth: '150px',
            cell: row => {
                const majorMeta = getMajorGroupMeta(row.moduleGroup || 'ungrouped');
                return (
                    <Chip
                        size="small"
                        label={row.majorGroupLabel || majorMeta.label}
                        sx={{
                            fontSize: '0.7rem',
                            height: 22,
                            color: majorMeta.color,
                            bgcolor: majorMeta.bgColor,
                            border: `1px solid ${majorMeta.borderColor}`,
                        }}
                    />
                );
            },
        },
        {
            name: 'SUB GROUP',
            selector: row => row.moduleGroupLabel || getModuleGroupLabel(row.moduleGroup || 'ungrouped'),
            sortable: true,
            minWidth: '190px',
            cell: row => (
                <Chip
                    size="small"
                    variant="outlined"
                    color={row.majorGroup === 'dashboard' ? 'primary' : 'default'}
                    label={row.moduleGroupLabel || getModuleGroupLabel(row.moduleGroup || 'ungrouped')}
                    sx={{ fontSize: '0.7rem', height: 22 }}
                />
            ),
        },
        {
            name: 'VISIBILITY',
            selector: row => row.is_public,
            sortable: true,
            width: '130px',
            cell: row => (
                row.is_public ? (
                    <Chip
                        icon={<PublicRoundedIcon />}
                        label="Public"
                        size="small"
                        color="success"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                ) : (
                    <Chip
                        icon={<LockRoundedIcon />}
                        label="Restricted"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                )
            ),
        },
        {
            name: 'USERS',
            selector: row => row.is_public ? Infinity : (row.allowed_users?.length || 0),
            sortable: true,
            width: '120px',
            cell: row => (
                row.is_public ? (
                    <Chip label="All" size="small" color="success" variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 22 }} />
                ) : (
                    <Chip
                        label={`${row.allowed_users?.length || 0} user${row.allowed_users?.length !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        color={row.allowed_users?.length ? 'default' : 'default'}
                        sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                )
            ),
        },
        {
            name: 'GROUPS',
            selector: row => row.is_public ? Infinity : (row.allowed_groups?.length || 0),
            sortable: true,
            width: '120px',
            cell: row => (
                row.is_public ? (
                    <Chip label="All" size="small" color="success" variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 22 }} />
                ) : (
                    <Chip
                        label={`${row.allowed_groups?.length || 0} group${row.allowed_groups?.length !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        color={row.allowed_groups?.length ? 'primary' : 'default'}
                        sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                )
            ),
        },
        {
            name: 'VIEWS',
            selector: row => row.view_count ?? 0,
            sortable: true,
            width: '90px',
            sortFunction: (a, b) => (a.view_count ?? 0) - (b.view_count ?? 0),
            cell: row => (
                <Typography variant="body2" color="text.secondary">
                    {row.view_count ?? 0}
                </Typography>
            ),
        },
        {
            name: '',
            cell: row => <RowActions row={row} onEditModule={onEditModule} />,
            ignoreRowClick: true,
            button: true,
            width: '56px',
        },
    ];

    return (
        <DataTable
            columns={columns}
            data={filteredModules || []}
            pagination
            highlightOnHover
            customStyles={tableStyles}
            progressPending={isLoading}
            progressComponent={<LoadingComponent />}
            expandableRows
            expandOnRowClicked
            expandableRowsComponent={ExpandedRow}
            noDataComponent={
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography color="text.secondary">No modules found.</Typography>
                </Box>
            }
        />
    );
}